// Control handoff (decision 0044): the player can hand control to another controller (a mount, a boat, a cart) and
// get it back, with the camera profile following. The handoff routes the player's input to whoever has control and
// says which camera profile that is; what a rider does while it doesn't (sit on the mount's seat) is the rider's
// mounted mode. The state is plain data, a stack of controllable IDs, so it saves and hashes with the rest.

/** Anything the player can control. */
export interface Controllable {
  /** A stable ID (the kernel entity's or the body's). */
  readonly id: string;
  /** The camera profile name while it has control ("explore", "mount"); the camera reads it. */
  readonly cameraProfile: string;
}

/** Who has control: the bottom is the player's own character, the top has control now. */
export interface HandoffState {
  stack: string[];
}

/** Planted defects (tests only). */
export interface HandoffFaults {
  /** `release` reports success but control never comes back: the modes spec must go red. */
  neverReturns: boolean;
}

/** A change of control, for a camera or a HUD. */
export interface HandoffChange {
  from: string;
  to: string;
  cameraProfile: string;
}

/** Routes control between the player's character and what it can ride or drive. */
export class ControlHandoff {
  readonly faults: HandoffFaults = { neverReturns: false };
  private readonly byId = new Map<string, Controllable>();
  private readonly listeners: ((c: HandoffChange) => void)[] = [];

  /** `home` is the player's own character: control starts and ends there. */
  constructor(readonly home: Controllable, others: readonly Controllable[] = []) {
    for (const c of [home, ...others]) this.register(c);
  }

  /** Add something the player can take control of. Throws on a taken ID. */
  register(c: Controllable): void {
    if (this.byId.has(c.id)) throw new Error(`handoff: "${c.id}" is registered twice`);
    this.byId.set(c.id, c);
  }

  /** A state with the home character in control. */
  init(): HandoffState {
    return { stack: [this.home.id] };
  }

  /** Who has control now. */
  owner(s: HandoffState): Controllable {
    return this.get(s.stack[s.stack.length - 1] ?? this.home.id);
  }

  /** Whether `id` has control now. */
  controls(s: HandoffState, id: string): boolean {
    return this.owner(s).id === id;
  }

  /** The camera profile of whoever has control. */
  cameraProfile(s: HandoffState): string {
    return this.owner(s).cameraProfile;
  }

  /** Hand control from whoever has it to `to`. Throws for an unknown ID or one already in the chain. */
  give(s: HandoffState, to: string): void {
    const next = this.get(to);
    if (s.stack.includes(to)) throw new Error(`handoff: "${to}" already has or holds control`);
    const from = this.owner(s);
    s.stack.push(next.id);
    this.emit({ from: from.id, to: next.id, cameraProfile: next.cameraProfile });
  }

  /** Give control back from the top of the chain to the one under it. Returns false at home (nothing to give back). */
  release(s: HandoffState): boolean {
    if (s.stack.length <= 1) return false;
    const from = this.owner(s);
    // the planted defect sits here, beside the pop it skips
    if (!this.faults.neverReturns) s.stack.pop();
    const to = this.owner(s);
    if (to.id !== from.id) this.emit({ from: from.id, to: to.id, cameraProfile: to.cameraProfile });
    return true;
  }

  /** Call `fn` on every change of control. Returns a remover. */
  onChange(fn: (c: HandoffChange) => void): () => void {
    this.listeners.push(fn);
    return () => {
      const i = this.listeners.indexOf(fn);
      if (i >= 0) this.listeners.splice(i, 1);
    };
  }

  private get(id: string): Controllable {
    const c = this.byId.get(id);
    if (!c) throw new Error(`handoff: no controllable "${id}"`);
    return c;
  }

  private emit(c: HandoffChange): void {
    for (const fn of this.listeners) fn(c);
  }
}
